import { logoGeometry } from '@avexa/config/logo'
import { type ReactNode, useId } from 'react'

import { useT } from '@/i18n'
import { cn } from '@/lib/utils'

export type WordmarkProps = {
  compact?: boolean
  className?: string
}

const { width, height, left, right } = logoGeometry

/** Rendered height of the mark, in px. Width follows from the 77:52 aspect. */
const MARK_HEIGHT = 20
const MARK_WIDTH = Math.round((MARK_HEIGHT * width) / height)

/**
 * The admin lockup: the client's mark, plus "{{product}} Admin" and the
 * "Control panel" line.
 *
 * ⚠️ THE MARK'S GEOMETRY IS NOT IN THIS FILE. It is in
 * `packages/config/src/logo.ts`, together with the colours, the derivation from
 * the client's artwork of 6 Oct 2026, and the open trademark question about its
 * resemblance to Mastercard's. Read that header before changing a coordinate.
 *
 * The three fills are the theme-aware custom properties the Tailwind preset
 * emits (`--logo-mark`, `--logo-mark-alt`, `--logo-mark-overlap`), so the mark
 * flips to its light-on-dark colourway with the panel's theme instead of
 * disappearing into a dark sidebar.
 *
 * ⚠️ `truncate` ON THE NAME IS LOAD-BEARING, AND THE NAME GOT LONGER. The
 * collapsed sidebar is `w-14` (56 px) and shows the mark alone; the expanded one
 * is `w-60` (240 px), which leaves ~182 px after the mark and the gap.
 * "Proffplassen Admin" measures ~124 px at `text-body-sm` semibold, so it fits —
 * but it is twelve characters where "Avexa" was five, and a longer product name
 * or a narrower sidebar would clip. Measure before changing either.
 */
export function Wordmark({ compact = false, className }: WordmarkProps): ReactNode {
  const t = useT()
  // Namespaced per instance: the sidebar, the mobile drawer and the login page
  // can all be mounted at once, and three identical `clipPath` ids in one
  // document is a correctness bug waiting for one of them to stop matching.
  const clipId = `${useId()}-logo-clip`

  return (
    <span className={cn('flex items-center gap-2 overflow-hidden', className)}>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        aria-hidden="true"
        focusable="false"
        className="shrink-0"
        width={MARK_WIDTH}
        height={MARK_HEIGHT}
      >
        <defs>
          <clipPath id={clipId}>
            <circle cx={left.cx} cy={left.cy} r={left.r} />
          </clipPath>
        </defs>
        <circle cx={left.cx} cy={left.cy} r={left.r} className="fill-logo-mark" />
        <circle cx={right.cx} cy={right.cy} r={right.r} className="fill-logo-mark-alt" />
        <circle
          cx={right.cx}
          cy={right.cy}
          r={right.r}
          clipPath={`url(#${clipId})`}
          className="fill-logo-mark-overlap"
        />
      </svg>
      {compact ? (
        <span className="sr-only">{t('app.name')}</span>
      ) : (
        <span className="flex min-w-0 flex-col leading-none">
          <span className="truncate text-body-sm font-semibold text-foreground">{t('app.name')}</span>
          <span className="truncate text-overline uppercase text-muted-foreground">
            {t('app.tagline')}
          </span>
        </span>
      )}
    </span>
  )
}
